/**
 * `/form/:id` serves two kinds of public form link:
 *
 * - a legacy signed token (always contains a `.`), pinned to one template
 *   version and subject, served by `/api/public/forms/:token`;
 * - a permanent INTAKE share key (never contains a `.`), which always serves
 *   the latest version and can be revoked by regenerating it, served by
 *   `/api/public/forms/intake/:key`.
 */
export type PublicFormSource =
  | { kind: 'signed-token'; token: string }
  | { kind: 'intake-key'; key: string };

export function resolvePublicFormSource(id: string): PublicFormSource {
  return id.includes('.') ? { kind: 'signed-token', token: id } : { kind: 'intake-key', key: id };
}

/** Base path for GET (form access); submissions POST to `${path}/submissions`. */
export function publicFormApiPath(source: PublicFormSource): string {
  return source.kind === 'signed-token'
    ? `/api/public/forms/${encodeURIComponent(source.token)}`
    : `/api/public/forms/intake/${encodeURIComponent(source.key)}`;
}

/**
 * The raw `embed` search value is kept exactly as shared (`1`, `true`, …) so
 * the router never rewrites the address bar; this decides what it means.
 * TanStack's default search parser JSON-decodes values, so `?embed=1` arrives
 * as the number 1 and `?embed=true` as the boolean.
 */
export function isEmbeddedSearchValue(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

export interface PublicFormSearch {
  appointmentId?: string;
  embed?: string | number | boolean;
}

/**
 * Search validation for `/form/:id`. It only ever returns keys that were
 * present in the URL, unchanged: adding a default (`embed: false`) or
 * normalising a value (`1` → `true`) makes the router rewrite the shared URL.
 */
export function parsePublicFormSearch(search: Record<string, unknown>): PublicFormSearch {
  const parsed: PublicFormSearch = {};
  if (typeof search.appointmentId === 'string') parsed.appointmentId = search.appointmentId;
  const embed = search.embed;
  if (typeof embed === 'string' || typeof embed === 'number' || typeof embed === 'boolean') {
    parsed.embed = embed;
  }
  return parsed;
}
